import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import CanvasDraw from 'react-canvas-draw';
import { X, Check, RotateCcw } from 'lucide-react';

const DoodleCanvas = ({ onSave, onClose }) => {
  const canvasRef = useRef(null);
  const [brushColor, setBrushColor] = useState('#111111');
  const [brushRadius, setBrushRadius] = useState(3);

  const colors = [
    '#111111',
    '#3B82F6',
    '#A7F3D0',
    '#D9C3F0',
    '#FFE58F',
    '#FDA4AF',
    '#34D399',
    '#818CF8'
  ];

  const handleSave = () => {
    if (canvasRef.current) {
      const dataUrl = canvasRef.current.getDataURL();
      onSave(dataUrl);
    }
  };

  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      style={{ pointerEvents: 'auto' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-2xl p-6 max-w-2xl w-full"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Draw a Doodle
          </h3>
          <button
            data-testid="close-doodle-button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-black/5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4 flex items-center gap-4 flex-wrap">
          <div className="flex gap-2">
            {colors.map((color) => (
              <button
                key={color}
                data-testid={`color-${color}-button`}
                onClick={() => setBrushColor(color)}
                className={`w-8 h-8 rounded-full border-2 hover:scale-110 transition-transform ${
                  brushColor === color ? 'ring-2 ring-offset-2 ring-[#3B82F6]' : ''
                }`}
                style={{ background: color, borderColor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: '#6B7280' }}>Brush:</span>
            <input
              data-testid="brush-size-slider"
              type="range"
              min="1"
              max="10"
              value={brushRadius}
              onChange={(e) => setBrushRadius(Number(e.target.value))}
              className="w-24"
            />
            <span className="text-sm font-medium" style={{ color: '#111111' }}>{brushRadius}px</span>
          </div>

          <button
            data-testid="clear-canvas-button"
            onClick={handleClear}
            className="ml-auto px-4 py-2 rounded-full font-medium hover:scale-105 active:scale-95 flex items-center gap-2"
            style={{ background: '#F9F9FB', color: '#111111', transition: 'transform 0.2s' }}
          >
            <RotateCcw className="w-4 h-4" />
            Clear
          </button>
        </div>

        <div
          className="rounded-2xl overflow-hidden border-2 border-dashed mb-4"
          style={{ borderColor: '#E5E7EB', background: '#F9F9FB' }}
        >
          <CanvasDraw
            ref={canvasRef}
            brushColor={brushColor}
            brushRadius={brushRadius}
            canvasWidth={600}
            canvasHeight={400}
            lazyRadius={0}
            hideGrid
            backgroundColor="#FFFFFF"
            style={{ width: '100%', height: 'auto' }}
          />
        </div>

        <div className="flex gap-3">
          <button
            data-testid="cancel-doodle-button"
            onClick={onClose}
            className="flex-1 py-3 rounded-full font-semibold hover:scale-105 active:scale-95"
            style={{ background: '#F9F9FB', color: '#111111', transition: 'transform 0.2s' }}
          >
            Cancel
          </button>
          <button
            data-testid="save-doodle-button"
            onClick={handleSave}
            className="flex-1 py-3 rounded-full font-semibold text-white shadow-md hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
            style={{ background: '#111111', transition: 'transform 0.2s' }}
          >
            <Check className="w-5 h-5" />
            Send Doodle
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DoodleCanvas;